import { useCallback } from 'react';
import { css } from '@emotion/react';
import { useHistoryControl } from '@frontend/history';
import { useTranslation } from '@frontend/i18n';
import { usePanelStore } from '@frontend/panel-engine';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { TitleBarButton } from '@frontend/shell-theme';
import { shell } from '@frontend/shell-utils';
import { theme } from '@frontend/theme';

const shellHistoryControlTrackingIdBase = 'shell-title-bar-history-control';
const backTrackingId = `${shellHistoryControlTrackingIdBase}-back-button`;
const forwardTrackingId = `${shellHistoryControlTrackingIdBase}-forward-button`;
const refreshTrackingId = `${shellHistoryControlTrackingIdBase}-refresh-button`;

export const HistoryControl = () => {
  const { back, forward, canGoBack, canGoForward } = useHistoryControl();
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const hasTrayPanels = usePanelStore((state) => state.hasTrayPanels);
  const closeAllPanels = usePanelStore((state) => state.closeAllPanels);

  const shouldOverrideHistoryControl = isXSmall && hasTrayPanels;
  const { t } = useTranslation('base');

  const handleAppRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const handleBackClick = useCallback(() => {
    if (shouldOverrideHistoryControl) {
      closeAllPanels();
      return;
    }
    back();
  }, [shouldOverrideHistoryControl, closeAllPanels, back]);

  return (
    <div
      css={[
        css`
          display: flex;
          align-items: center;
          margin-left: ${shell.isMac ? '100px' : '20px'};
          gap: ${theme.spacing(0.5)};
          -webkit-app-region: no-drag;
        `,
        !shell.isWindows &&
          css`
            margin-right: auto;
          `,
      ]}
    >
      <TitleBarButton
        onClick={handleBackClick}
        hasBorder={false}
        disabled={shouldOverrideHistoryControl ? false : !canGoBack}
        label={t('Go Back')}
        trackingId={backTrackingId}
        icon='back-small'
      />
      <TitleBarButton
        onClick={() => forward()}
        hasBorder={false}
        disabled={!canGoForward}
        label={t('Go Forward')}
        trackingId={forwardTrackingId}
        icon='forward-small'
      />
      <TitleBarButton
        onClick={handleAppRefresh}
        hasBorder={false}
        label={t('Reload App')}
        trackingId={refreshTrackingId}
        icon='update-small'
      />
    </div>
  );
};
